<template>
	<div>
		<Maintenance v-if="isMaintenanceMode" />
		<template v-else>
			<div class="app__router-outlet">
				<router-view name="header"></router-view>
				<router-view></router-view>
			</div>
		</template>
		<Popup />
		<Toast />
		<ContextMenuHost />
		<TooltipHost />
		<Toolbar />
		<SessionExpiringPrompt />
	</div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import { ModuleRole } from '@/common/rbac';
import { EventBus } from './event-bus';
import UpdatePassword from './components/Dashboard/Views/Pages/UpdatePassword/UpdatePassword.vue';
import Maintenance from './components/UIComponents/Maintenance/Maintenance.vue';
import SessionExpiringPrompt from './components/UIComponents/SessionExpiringPrompt.vue';
import {
	KillWebsocket,
	StartNotificationWebsocket,
} from './websockets/notifications';
import { makeCallToVersionAPI } from '@/worker/worker';
import {
	hasValueChanged,
	updateValueAndShowModal,
} from '@/worker/getVersionNumber';
import { cleanApiKeysCache, setupClaims } from './controller/apikeys';
import config from '@/config';
import { cleanUserIdCache } from './controller/userId';
import { useAuthStore } from '@/stores/auth';
import { useCaseDetailsStore } from '@/stores/caseDetails';
import { useUserStore } from '@/stores/user';
import { Popup, popupService } from '@arritech/ui/Popup';
import { Toolbar } from '@arritech/ui/Toolbar';
import { Toast, toast } from '@arritech/ui/Toast';
import type { ToastOptions } from '@arritech/ui/Toast';
import { ToastType } from '@arritech/ui/Toast';
import { ContextMenuHost } from '@arritech/ui/ContextMenu';
import { TooltipHost } from '@arritech/ui/Tooltip';
import { isNoAuthRoute } from '@/utils/routeAccess';
import { endExpiredSession } from '@/utils/sessionExpiry';

const TOKEN_EXPIRY_INTERVAL_MS = 2000;
const VERSION_POLL_INTERVAL_MS = 300000;
const NOTIFICATION_WS_RETRY_MS = 5000;
const PASSWORD_UPDATE_POPUP_ID = 'update-password-popup';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const caseDetailsStore = useCaseDetailsStore();

let tokenExpiryIntervalFunction: ReturnType<typeof setInterval> | null = null;
let workerInterval: ReturnType<typeof setInterval> | null = null;
let eventListenerAdded = false;
const showNotification = ref(true);

const passwordNeedsUpdate = computed(() => authStore.passwordNeedsUpdate);
const isSessionExpired = computed(() => authStore.isSessionExpired());
const isMaintenanceMode = computed(() => config.maintenanceEnv === 'true');

const handleSessionExpiration = () => {
	KillWebsocket();
};

const openUpdatePasswordPopup = () => {
	if (!authStore.isAuthenticated()) {
		return;
	}

	popupService.showPopup({
		id: PASSWORD_UPDATE_POPUP_ID,
		title: t('auth.updatePassword.title'),
		component: UpdatePassword,
		config: {
			background: true,
			position: 'center',
			canClose: false,
			width: '760px',
		},
	});
};

const closeUpdatePasswordPopup = () => {
	popupService.close({ id: PASSWORD_UPDATE_POPUP_ID });
};

const initialiseVersionInStore = () => {
	authStore.setVersion();
};

const clearLocalStorageVersion = () => {
	localStorage.removeItem('version'); // to be removed later once everyone is updated
};

const unregisterServiceWorkers = () => {
	if (navigator.serviceWorker) {
		navigator.serviceWorker.getRegistrations().then((registrations) => {
			for (const registration of registrations) {
				registration.unregister();
			}
		});
	}
};

const clearBrowserCaches = () => {
	if (window.caches) {
		window.caches
			.keys()
			.then((keyList) =>
				Promise.all(keyList.map((key) => window.caches.delete(key)))
			);
	}
};

const initialSetup = () => {
	if (authStore.isAuthenticated()) {
		authStore.loadMerchantDetails();
		// Login fetches the plan's module flags; a session that predates them, or
		// whose fetch failed, has an empty map. Ask again on load so the sidebar
		// lock and the route guard are not left open for the rest of the session.
		if (!Object.keys(authStore.moduleAvailability).length) {
			authStore.fetchModuleAvailability();
		}
	}
	unregisterServiceWorkers();
	clearBrowserCaches();
	initialiseVersionInStore();
};

const setupWorkerInterval = () => {
	if (workerInterval) {
		clearInterval(workerInterval);
	}

	workerInterval = setInterval(() => {
		makeCallToVersionAPI().then(
			({ version, status }: { version: string; status: number }) => {
				if (
					showNotification.value &&
					hasValueChanged(version) &&
					status === 200
				) {
					showNotification.value = false;
					updateValueAndShowModal(version);
				}
			}
		);
	}, VERSION_POLL_INTERVAL_MS);

	if (!eventListenerAdded) {
		document.addEventListener('visibilitychange', () => {
			if (document.visibilityState === 'visible') {
				setupWorkerInterval();
			}
		});
		eventListenerAdded = true;
	}
};

type WsNotification = {
	title?: string;
	content?: string;
	base64Content?: string;
	type?: string;
	severity?: string;
	referencePayload?: { actionURL?: string };
};

const handleNotification = (raw: MessageEvent) => {
	const message: WsNotification = JSON.parse(raw.data).data;
	// Legacy toasts carry a GENERAL/ALERT/INFO `type`; new notification-platform
	// toasts carry the definition's `severity`, which drives the toast colour.
	const typeMap: Record<string, ToastType> = {
		GENERAL: 'success',
		ALERT: 'warning',
		INFO: 'info',
	};
	const severityMap: Record<string, ToastType> = {
		Success: 'success',
		Critical: 'error',
		Warning: 'warning',
		Info: 'info',
	};
	const toastType = message.severity
		? severityMap[message.severity]
		: typeMap[message.type ?? ''];

	const rawContent = message.content
		? message.content
		: message.base64Content
			? window.atob(message.base64Content)
			: '';

	const stripHtml = document.createElement('div');
	stripHtml.innerHTML = rawContent;
	const content = stripHtml.textContent || stripHtml.innerText || '';

	const toastConfig: ToastOptions = {
		title: message.title,
		message: content,
		type: toastType,
		dismissible: true,
		duration: 10000,
	};

	// New-platform toasts carry a deep-link in referencePayload.actionURL;
	// surface it as an action button. Legacy toasts have none.
	const actionURL = message.referencePayload?.actionURL;
	if (actionURL) {
		toastConfig.action = {
			label: t('notifications.common.actions.openRelated'),
			onClick: () => router.push(actionURL),
		};
	}

	toast(toastConfig);

	// Real-time unread badge: a user-targeted notification toast means a new
	// unread item, so bump the combined badge immediately. The authoritative
	// dual-source count (HistoryTable/Dashboard sync) reconciles any drift;
	// e.g. transient ToastOnly notifications, which have no inbox record and
	// so should not persist in the count. (A displayMode in the toast payload
	// would let us exclude those exactly; FE-only for now.)
	const current = Number(authStore.getUnreadNotificationsNo || 0);
	authStore.setUnreadNotificationsNo(current + 1);
};

const handleWsMessage = (raw: MessageEvent) => {
	if (raw.data === 'PONG') {
		return;
	}

	let parsedMessage = null;
	try {
		parsedMessage = JSON.parse(raw.data);
	} catch {
		console.log('Unknown message received from websocket');
		console.log(raw);
		return;
	}

	const event = parsedMessage?.name;
	const payload = parsedMessage?.data;

	switch (event) {
		case 'send_notification_event':
			handleNotification(raw);
			break;
		case 'clean_users_map_cache_event':
			authStore.loadMerchantDetails();
			useUserStore().fetchAllUsers({ force: true });
			cleanUserIdCache();
			cleanApiKeysCache();
			break;
		case 'send_broadcast_case_status_event': {
			if (route.name !== 'Case Details') {
				return;
			}
			const caseId = payload;
			const query = route.query;
			// Nothing to refetch unless this is the case currently on screen.
			if (!query.id || !caseId || query.id !== caseId) {
				return;
			}
			caseDetailsStore.fetchCaseDetailsById({
				caseId,
				findPayload: {
					page: 1,
					limit: Number.MAX_SAFE_INTEGER,
					search: '',
					orderBy: 'created_by.timestamp',
					orderByDesc: true,
				},
			});
			// Face-comparison results live in the case entities, not caseDetails,
			// so refresh those too; this is what lets the selfie "Comparing…" card
			// resolve to its result without a manual reload.
			caseDetailsStore.setRefreshEntities(true);
			break;
		}
		case 'broadcast_comply_batch_search_delete_event':
			// The completion toast is now delivered by the notification platform
			// (comply.batch.delete.completed); this event only refreshes the list.
			if (route.name === 'AML History') {
				EventBus.$emit('refresh-aml-search-history');
			}
			break;
		case 'cfm_questions_extraction_ready_event':
			if (route.name === 'Form Templates') {
				EventBus.$emit(
					'cfm-questions-extraction-ready',
					JSON.parse(raw.data).data
				);
			}
			break;
		case 'send_broadcast_topup_completed_event':
			EventBus.$emit('topup-completed', payload);
			break;
		default:
			console.log('Unknown message received from websocket');
			console.log(raw);
			break;
	}
};

const connectNotificationWs = () => {
	const notificationTimer = setInterval(() => {
		if (
			authStore.isAuthenticated() &&
			!authStore.isSessionExpired() &&
			!authStore.needs2FAVerification()
		) {
			const ws = StartNotificationWebsocket(localStorage.getItem('session_id'));
			if (!ws) {
				return;
			}
			if (ws.onmessage !== null) {
				clearInterval(notificationTimer);
				return;
			}
			ws.onmessage = handleWsMessage;
			clearInterval(notificationTimer);
		}
	}, NOTIFICATION_WS_RETRY_MS);
};

const setupTokenExpiryCheck = () => {
	tokenExpiryIntervalFunction = setInterval(() => {
		if (authStore.isAuthenticated()) {
			return;
		}
		if (isNoAuthRoute(route)) {
			return;
		}
		if (route.name !== 'Login' && !authStore.isAuthenticated()) {
			void endExpiredSession(router);
		}
	}, TOKEN_EXPIRY_INTERVAL_MS);
};

const setupStorageListener = () => {
	window.addEventListener('storage', (event) => {
		if (event.key === 'syncTokenRefreshEvent' && event.newValue) {
			const data = JSON.parse(event.newValue);
			if (authStore && typeof authStore.syncTokenRefresh === 'function') {
				authStore.syncTokenRefresh(data);
			}
		}
	});
};

const removeConsoleLogs = () => {
	if (config.disableLogsEnv === 'true') {
		console.log = function () {};
		console.dir = function () {};
	}
	console.log(
		'%cLogs are enabled',
		'color: white; background-color: green; font-size: 14px; padding: 10px; border-radius: 5px; font-weight: bold; text-align: center; margin: 10px 0 20px 0;'
	);
};

watch(isSessionExpired, (newValue) => {
	if (newValue === true) {
		handleSessionExpiration();
	}
});

watch(
	passwordNeedsUpdate,
	(value) => {
		if (value) {
			openUpdatePasswordPopup();
		} else {
			closeUpdatePasswordPopup();
		}
	},
	{ immediate: true }
);

onMounted(() => {
	removeConsoleLogs();
	clearLocalStorageVersion();
	initialSetup();
	setupWorkerInterval();
	connectNotificationWs();
	setupTokenExpiryCheck();
	setupStorageListener();
	setupClaims(authStore.can(ModuleRole.PlatformUser));
});

onBeforeUnmount(() => {
	if (tokenExpiryIntervalFunction) clearInterval(tokenExpiryIntervalFunction);
	if (workerInterval) clearInterval(workerInterval);
});
</script>
<style lang="scss">
@use '@arritech/ui/styles/global.scss';

$secondary-color: var(--color-secondary);

.app__router-outlet {
	position: relative;
}

// Loader utilities shared across views. .centered-loader pins the spinner to the
// centre of the viewport for full-page loads; .table-loader keeps it in flow,
// centred horizontally, for loaders inside a table.
.centered-loader {
	position: fixed;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	z-index: 1000;
}

.table-loader {
	margin: 0 auto;
}

.update-password-container {
	display: flex;
	justify-content: center;
	align-items: center;
	min-height: 100vh;
}

.custom-button:hover {
	background-color: $secondary-color !important;
}

.custom-button:disabled {
	cursor: not-allowed !important;
}

// Multiselect overwrite

.multiselect__input,
.multiselect__single {
	font-size: var(--font-size-s) !important;
}
</style>
